<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else style="max-width: 700px">
    <div class="title-screen">
      <span id="screen-header">{{ $adminT('events.info', 'Event info') }}</span>
    </div>
    <theater-event-info-form
      :vs="$v.form"
      :show-update-button="true"
      :initial-values="form"
      :show-title="false"
      :organization-slug="organizationSlug"
      :categories="allEventCategories"
      :enable-publishing="enablePublishing"
      :publishing-in-progress="publishingInProgress"
      @update="handleUpdate($event)"
    />
    <simple-snackbar
      :open.sync="snackbarOpen"
      :message-prop="workflowLabel(localizedSnackbarMessage)"
      :color="snackbarColor"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { maxLength, required } from 'vuelidate/lib/validators'
import { httpUrl } from '@/validators'

import {
  GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
  UPDATE_EVENT_TEMPLATE_AND_PERFORMANCE,
} from '@/graphql/eventTemplate.graphql'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar'
import Spinner from '../shared/spinner'
import TheaterEventInfoForm from './theaterEventInfoForm.vue'
import { submitGuard } from '@/utils'
import { GET_ALL_EVENT_CATEGORIES } from '@/graphql/eventCategory.graphql'
import { omit } from 'lodash'

export default {
  mixins: [WorkflowDisplay],
  name: 'event-info-container',
  components: {
    SimpleSnackbar,
    Spinner,
    TheaterEventInfoForm,
  },
  props: {
    eventTemplateId: {
      type: String,
      default: null,
    },
    organizationSlug: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarColor: 'primary',
      form: {},
      allEventCategories: [],
      enablePublishing: false,
      publishingInProgress: false,
      isEventPublished: false,
    }
  },
  validations() {
    return {
      form: {
        title: { required, maxLength: maxLength(120) },
        genreId: {
          required,
        },
        durationInMins: {
          required,
        },
        instagramLink: {
          httpUrl,
        },
        webLink: {
          httpUrl,
        },
        facebookLink: {
          httpUrl,
        },
      },
    }
  },
  computed: {
    localizedSnackbarMessage() {
      switch (this.snackbarMessage) {
      case 'Fill in the required information': return this.$adminT('events.requiredInfo', 'Fill in the required information')
      case 'Event details saved and will be live in 20 minutes.': return this.$adminT('events.savedPublished', 'Event details saved and will be live in 20 minutes.')
      case 'Event details saved.': return this.$adminT('events.saved', 'Event details saved.')
      case 'Something went wrong. Please try again.': return this.$adminT('events.tryAgain', 'Something went wrong. Please try again.')
      default: return this.snackbarMessage
      }
    },
  },
  methods: {
    async handleUpdate(publishChanges) {
      this.$v.form.$touch()
      if (this.$v.form.$invalid === true) {
        this.snackbarOpen = true
        this.snackbarMessage = 'workflowsAll.fill_in_the_required_information_a32c330'
        this.snackbarColor = 'error'

        return
      }
      await submitGuard(this, async () => {
        if (publishChanges) {
          this.publishingInProgress = true
        }
        // VAT is managed from the performances view, not here — never send it from event-info.
        const { __typename, organization, VATPercentage, ...eventTemplateInput } = this.form
        await this.$apollo.mutate({
          mutation: UPDATE_EVENT_TEMPLATE_AND_PERFORMANCE,
          variables: {
            organizationSlug: this.organizationSlug,
            eventTemplateInput,
            publishChanges,
          },
        })

        this.snackbarOpen = true
        this.snackbarMessage = (publishChanges ? 'workflowsAll.event_details_saved_and_will_be_live_in_20_mi_70961ff' : 'workflowsAll.event_details_saved_034f903')
        this.snackbarColor = 'primary'
        this.publishingInProgress = false

        if (!this.isEventPublished) {
          window.location.reload()
        }
      }).catch(() => {
        this.snackbarOpen = true
        this.snackbarMessage = 'workflowsAll.something_went_wrong_please_try_again_9a3ea05'
        this.snackbarColor = 'error'
        this.publishingInProgress = false
      })
    },
  },
  apollo: {
    getOrganizationEventTemplate: {
      query: GET_ORGANIZATION_EVENT_TEMPLATE_AND_LOGGED_USER,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        }
      },
      manual: true,
      result({ data: { getOrganizationEventTemplate, me }, loading }) {
        if (!loading && getOrganizationEventTemplate) {
          this.form = omit(getOrganizationEventTemplate, ['events', 'organization'])
          this.form.interests =
            [
              ...new Set(
                getOrganizationEventTemplate.events.flatMap(event =>
                  event.interests.map(interest => interest.id),
                ),
              ),
            ] || []
          this.isEventPublished = getOrganizationEventTemplate.events.some(
            event => event.status === 'PUBLISHED',
          )

          const { flags } = me
          this.enablePublishing =
            flags && flags.allowPublishEventsToLanding && this.isEventPublished
        }
      },
    },
    getAllEventCategories: {
      query: GET_ALL_EVENT_CATEGORIES,
      result({ data, loading }) {
        if (!loading && data) {
          this.allEventCategories = data.getAllEventCategories.sort((a, b) => a.id - b.id)
        }
      },
    },
  },
}
</script>

<style scoped></style>
